/**
 * Tally parent group tree API
 * GET /api/gl-maps/parent-groups/tree
 */

import { getStoredToken } from "../../../../services/api";
import { API_BASE_URL } from "../../../../config/api";
import type { AccountTreeResponseDTO } from "../../../../shared/data/models/AccountTreeDTOs";

class APIError extends Error {
  public status: number;
  public data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
};

async function getToken(): Promise<string> {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }
  return token;
}

export async function fetchGlParentGroupTree(params: {
  accountType: string;
  search?: string;
  limit?: number;
  offset?: number;
  selectedAccountId?: string;
}): Promise<AccountTreeResponseDTO> {
  const token = await getToken();
  const query = new URLSearchParams();
  query.set("account_type", params.accountType);
  if (params.search) {
    query.set("search", params.search);
    query.set("limit", String(params.limit ?? 50));
    query.set("offset", String(params.offset ?? 0));
  }
  if (params.selectedAccountId) {
    query.set("selected_account_id", params.selectedAccountId);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/gl-maps/parent-groups/tree?${query.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      (data as { message?: string }).message ||
        `Failed to fetch parent group tree: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as AccountTreeResponseDTO;
}
